<template>
    <div id = "map">
      <InfoFenster style="z-index: 9999;" v-show="clickInfoMain.visible" 
      :info="clickInfoMain" 
      @show-einzugsgebiet="highlightEinzugsgebiet" 
      @hide-einzugsgebiet="unhighlightEinzugsgebiet">
    </InfoFenster>
    <div class="homeicon"><a target="_blank" :href="wvconfig.config.value?.logoLink ? wvconfig.config.value?.logoLink : 'https://www.hydrotec.de'"><img draggable="false" class="homeicon" alt="Bild nicht gefunden" id="homeicon" :src="wvconfig.config.value?.logo ? wvconfig.config.value?.logo : 'icon/Hydrotec-Logo.svg'"/></a></div>
    </div>
</template>
 
<script lang="ts">
import { Feature, Map, View } from 'ol';
import { Vector as VectorLayer, Tile as TileLayer } from 'ol/layer';
import { Vector as VectorSource, OSM } from 'ol/source';
import GeoJSON from "ol/format/GeoJSON";
import { onMounted, ref, computed, watch, shallowRef, unref } from 'vue';
import type { Ref } from 'vue';
import type { systemElement } from './Systemelement';
import apifetch from '../../../vueforms/src/utils/apifetch';
import InfoFenster from './InfoFenster.vue';
import Constants from "./Constants";
import WKT from 'ol/format/WKT';
import LineString from 'ol/geom/LineString.js';
import { Point } from 'ol/geom';
import { setKfStyle, setKfSelectStyle, setBauwerkSelectStyle, setBauwerkStyle, systemPlanStyleHaupt, systemPlanStyleNeben, tgStyle, teStyle, tgSelectStyle, tgHalfSelectStyle, teSelectStyle, gwStyle, gwSelectStyle, hoverMarkerStyle } from './Styles';
import { isEmpty, createEmpty, extend, boundingExtent } from 'ol/extent';
import { ClickInfo } from './ClickInfo';
import { ScaleLine, defaults as defaultControls } from 'ol/control';
import { Style, Text, Fill, Stroke } from 'ol/style';
import * as baseConfig  from "../../src/js/baseConfig";

export default {
  props: {
    layerVisibility: Object,
    abfluss: Object,
    wvconfig: {
      type: Object,
      required: true
    },
    selectedBackgroundLayer: {
      type: Object,
      required: false
    },
    additionalLayers: {
      type: Array,
      required: false
    },
    backgroundlayers: {
      type: Array,
      required: false
    },
    toggleAdditionalLayer: {
      type: Object,
      required: false
    },
    selectedData: {
      type: Object,
      required: true
    }
  },
  components: {
    InfoFenster,
  },
  emits: [
    "noGeometry", 
    "gewaesserClicked",
    "clickInMap",
    "gwHover",
    "gwClickMarker",
    "availableLayers"
  ],
  setup(props, {emit}){
    var map : Map;
    let clickInfoMain : Ref<ClickInfo> = ref(new ClickInfo());
    let clickInfoStore : ClickInfo;
    let selectedGewaesserFeature = computed(() => {
      if (props.selectedData.getGewaesser())
        return gwVectorSource.getFeatureById(props.selectedData.getGewaesser());
      else return null;
    })
    let seMap; // wird von mapSystemelements in der init Funktion gefüllt
    let availableLayers: Ref<Array<number>> = shallowRef([]) 
    const backgroundlayers = shallowRef([]);

    /**
     * Die Funktion highlightEinzugsgebiet wird von einem Button eines Transportelements oder Bauwerks im Infofenster ausgelöst  
     * und findet alle Teileinzugsgebiete bzw. kanal. Flächen, die zu dem Element entwässern, um diese zu highlighten.
     * @param SEBezeichnung Die ID_SEBezeichnung des jeweiligen Transportelements oder Bauwerks
     */

    function highlightEinzugsgebiet(SEBezeichnung: string, seTyp: number) {
      //Vorherige Selektion speichern
      clickInfoStore = Object.assign(Object.create(Object.getPrototypeOf(clickInfoMain.value)), clickInfoMain.value);
      //clickInfoMain bis auf Gerinne leeren
      clickInfoMain.value.emptyAll();
      let feature;
      if (seTyp === Constants.SETypen.Transportelement) {
        feature = teVectorSource.getFeatureById(SEBezeichnung);
        clickInfoMain.value.pushTransportelement(feature);
        feature?.setStyle(teSelectStyle);
      } else if (seTyp === Constants.SETypen.Bauwerk) {
        feature = bauwerkeVectorSource.getFeatureById(SEBezeichnung);
        clickInfoMain.value.pushBauwerk(feature);
        feature?.setStyle(setBauwerkSelectStyle(feature));
      }

      clickInfoMain.value.weiterfuehrendeAktion = true;

      let toDoList = seMap[SEBezeichnung].O_Ref.ALL.map((se) => se); 
      let ergebnisList = seMap[SEBezeichnung].O_Ref.ALL.map((se) => se);
      while (toDoList.length > 0) {
        let nextSE = toDoList.pop();
        for (let se of nextSE.O_Ref.ALL) {
          if (toDoList.indexOf(se) === -1)
            toDoList.push(se);
          if (ergebnisList.indexOf(se) === -1) {
            ergebnisList.push(se);
          }
        }
      }
      let extent = createEmpty();
      clickInfoMain.value.setEinzugsgebiet(SEBezeichnung, seTyp);
      //Teilgebiete-Features finden zum highlighten
      for (let i = 0; i < ergebnisList.length; i++) {
        let teilgebiet;
        if (seTyp == Constants.SETypen.Transportelement) {
          teilgebiet = tgVectorSource.getFeatureById(ergebnisList[i].ID_SEBezeichnung);
          teilgebiet?.setStyle(tgSelectStyle);
        } else if (seTyp == Constants.SETypen.Bauwerk) {
          teilgebiet = kFVectorSource.getFeatureById(ergebnisList[i].ID_SEBezeichnung);
          teilgebiet?.setStyle(setKfSelectStyle(teilgebiet));
        }
        if (teilgebiet) {
          clickInfoMain.value.pushTGForEinzugsbebiet(teilgebiet, seTyp);
          extend(extent, teilgebiet.getGeometry().getExtent());
        }
      }
      if (seTyp == Constants.SETypen.Transportelement) {
        //Zuletzt das Teilgebiet des Transportelements hinzufügen und schwächer highlighten. 
        //Es heißt immer genauso wie das Transportelements selbst, denn sie gehören zum selben Systemelement (auch in anderen Modellen so, richtig?)
        let tg = tgVectorSource.getFeatureById(SEBezeichnung);
        if (tg) {
          tg?.setStyle(tgHalfSelectStyle);
          extend(extent, tg.getGeometry().getExtent());
          clickInfoMain.value.pushTGForEinzugsbebiet(tg);
        }
      } else {
        if (!isEmpty(extent)) { //Nur wenn Einzugsteilgebiete gefunden wurden, die Koordianten des Bauwerks hinzufügen und zoomen
          extend(extent, boundingExtent([feature?.getGeometry().getCoordinates()]));
        }
      }
      
      //Zoom auf die gehighlighteten Gebiete
      if (!isEmpty(extent)) {
        map.getView().fit(extent, {duration: 1000, padding: [50, 50, 50, 50]});
      }
    }

    /**
     * Die Funktion wird aufgerufen, wenn ein toggle für das Anzeigen des Einzugsgebiets
     * auf "off" gestellt wird.
     */
    function unhighlightEinzugsgebiet() {
      clickInfoMain.value.emptyEinzugsgebiet();
      clickInfoMain.value = clickInfoStore;
      clickInfoMain.value.highlightSelection();
      map.getView().fit(tgVectorSource.getExtent(), {duration: 1000});
    }

    /**
     * Hilfsfunktion für getSystemplan
     * Liefert die letzten koordinaten, um eine Linie zu zeichnen
     * @param U letztes Unterlieger Systemelement (aus Map, die von mapSystemelements generiert wird), zu dem die Linie gezeichnet wird
     * @param km Einleitungsstelle am Gewässer
     */
    function getLastCoordinate(U, km) {
      //Wenn U ein Bauwerk ist 
      if (U.ID_TE_Typ.includes("Speicher") || U.ID_TE_Typ === "Kläranlage" || U.ID_TE_Typ.includes("Abzweig")) {
        return U.geometry.coordinates;
      } else {
        //Wenn U eine Gewässerstrecke ist
        let te = teVectorSource.getFeatureById(U.ID_SEBezeichnung);
        if (!te) {
          return undefined;
        }
        let gewaesser = te?.get('gewaesserNr');
        let gewaesserFeature = gwVectorSource.getFeatureById(gewaesser);

        if (!km && te.getGeometry()) { //Fall Hauptabfluss oder Stelle nicht gesetzt
          return te.getGeometry().getLastCoordinate();
        }
        else if (!gewaesserFeature && te.getGeometry()) {
          return te.getGeometry().getLastCoordinate();
        }
        else if (gewaesserFeature && km !== null && km !== undefined) {
          return gewaesserFeature.getGeometry().getCoordinateAtM(km, true);
        }
        return undefined;
      }
    }

    /**
     * Hilfsfunktion für getSystemplan
     * Sammle Knickstellen-Koordinaten von Kanälen zwischen den Bauwerken
     * @param U erster Unterlieger
     * @param knickstellen Array von Koordinaten
     */
    function sammleKnickstellen(U, knickstellen) {
      while (U && (U.ID_TE_Typ && 
                  (U.ID_TE_Typ === "Eikanal" || 
                  U.ID_TE_Typ === "Hufeisenkanal" ||
                  U.ID_TE_Typ === "Kanal mit Fließzeit" ||
                  U.ID_TE_Typ === "Kastenkanal" ||
                  U.ID_TE_Typ === "Kreiskanal" ||
                  U.ID_TE_Typ === "Trapezkanal")
                || !U.geometry)) {
        if (U.geometry) {
          knickstellen.push(U.geometry.coordinates);
        }
        U = U.U_Ref.ID_Abflussziel
      }
      return U;
    }
    /**
     * Hilfsfunktion für getSystemplan
     * @param U erster Unterlieger
     * @param seCoordinates Koordinaten des Bauwerks, von dem aus die Linie gezeichnet werden soll
     * @param km Km im Gewässer, an dem das Wasser einfließt
     * @param style Der Style für die Linie (gestrichelt oder durchgezogen)
     */
    function zeichneAbfluss(U, seCoordinates, km, style) {
      let knickstellen = [ seCoordinates ];
      let lastU = sammleKnickstellen(U, knickstellen);
      if (!lastU || !lastU.ID_TE_Typ) {
        return;
      }
      let lastCoords = getLastCoordinate(lastU, km);
      if (!lastCoords) {
        return;
      }
      knickstellen.push(lastCoords);
      let line = new LineString(knickstellen, "XY");
      let lineFeature = new Feature({ geometry: line });       
      lineFeature.setStyle(style);
      sysPlanVectorSource.addFeature(lineFeature);
    }

     /**
     * Zeichnet den Systemplan der Bauwerke ein
     */
    function getSystemplan(systemElements) {
      for (let se of Object.keys(systemElements)) {
        if (systemElements[se].ID_TE_Typ && systemElements[se].geometry) {
          if (systemElements[se].ID_TE_Typ.includes("Speicher") || 
              systemElements[se].ID_TE_Typ === "Kläranlage" || 
              systemElements[se].ID_TE_Typ.includes("Abzweig")) {
            const curr = systemElements[se];
            zeichneAbfluss(curr.U_Ref.ID_Abflussziel, curr.geometry.coordinates, curr.ID_SEKmAbfluss, systemPlanStyleHaupt);
            zeichneAbfluss(curr.U_Ref.ID_TE_SpeicherZielEntnahme, curr.geometry.coordinates, curr.ID_SpeicherKmEntnahme, systemPlanStyleNeben);
            zeichneAbfluss(curr.U_Ref.ID_TE_SpeicherZielUeberlauf, curr.geometry.coordinates, curr.ID_SpeicherKmUeberlauf, systemPlanStyleNeben);
            zeichneAbfluss(curr.U_Ref.ID_TE_AbzweigZielNebenabfluss, curr.geometry.coordinates, curr.ID_AbzweigKmSeitab, systemPlanStyleNeben);
          }
        }
      }
    }
    



    function highlightGewaesser(gewaesserNr: string | undefined) {
      if (!gewaesserNr) { return; }
      let feat = gwVectorSource.getFeatureById(gewaesserNr)
      if (!feat) {
        console.log("Zu dem Gewässer " + gewaesserNr + " ist keine Geometrie vorhanden");
        emit("noGeometry");
        return;
      }
      feat.setStyle(gwSelectStyle);
    }

    function unhighlightGewaesser(gewaesserNr: string | undefined) {
      if (!gewaesserNr) { return; }
      let feat = gwVectorSource.getFeatureById(gewaesserNr)
      if (!feat) {
        return;
      }
      feat.setStyle(gwStyle);
    }

    watch(() => props.selectedData.getGewaesser(), (newGewaesser, oldGewaesser) => {
      highlightGewaesser(newGewaesser);
      unhighlightGewaesser(oldGewaesser);
      if (newGewaesser && gwVectorSource.getFeatureById(newGewaesser)) {
        clickInfoMain.value.setGewaesser(gwVectorSource.getFeatureById(newGewaesser));
        clickInfoMain.value.setVisible();
      }
      clickInfoMain.value.setGewaesserKm(undefined);
      clickInfoMain.value.setAbfluss(undefined);
      hoverMarkerSource.clear();
    });

    watch(() => props.abfluss, (newVal)=> {
      clickInfoMain.value.setAbfluss(newVal);
    }, {deep: true})

    watch(() => props.selectedData.getMovingKm(), (newX, oldX) => {
      if(!props.selectedData.getGewaesser() || !selectedGewaesserFeature.value) {
        return;
      }
      hoverMarkerSource.clear();
      let coords = selectedGewaesserFeature.value?.getGeometry().getCoordinateAtM((newX * 1000), true);
      let point = new Point(coords, "XYM");
      let marker = new Feature({ geometry: point });
      hoverMarkerSource.addFeature(marker);
    });

    watch(() => props.selectedData.getFixedKm(), (newVal) => { 
      if(!props.selectedData.getGewaesser() || !selectedGewaesserFeature.value || !newVal) {	
        return;
      }
      gwKmMarkerSource.clear();
      let coords = selectedGewaesserFeature.value?.getGeometry().getCoordinateAtM((newVal.km * 1000), true);
      clickInfoMain.value.setGewaesserKm(newVal.km);
      clickInfoMain.value.setAbfluss(newVal.abfluss);
      let point = new Point(coords, "XYM");
      let marker = new Feature({ geometry: point });
      gwKmMarkerSource.addFeature(marker);
    })

    watch(() => props.layerVisibility, (newVal) => { 
      if (newVal.lastChange === "bauwerke") {
        bauwerkeLayer.setVisible(newVal.bauwerke);
        return;
      } else if (newVal.lastChange === "systemPlan") {
        sysPlanLayer.setVisible(newVal.systemPlan);
        return;
      } else if (newVal.lastChange === "transportelemente") {
        teLayer.setVisible(newVal.transportelemente);
        return;
      } else if (newVal.lastChange === "gewaesser") {
        gwLayer.setVisible(newVal.gewaesser);
        return;
      } else if (newVal.lastChange === "teilgebiete") {
        tgLayer.setVisible(newVal.teilgebiete);
        return;
      } else if (newVal.lastChange === "kanalFlaechen") {
        kFLayer.setVisible(newVal.kanalFlaechen);
        return;
      } else if (newVal.lastChange === "teilgebieteLabel") {
        tgLabelLayer.setVisible(newVal.teilgebieteLabel);
        return;
      } else if (newVal.lastChange === "kanalFlaechenLabel") {
        kfLabelLayer.setVisible(newVal.kanalFlaechenLabel);
        return;
      } else if (newVal.lastChange === "gewaesserLabel") {
        gwLabelLayer.setVisible(newVal.gewaesserLabel);
        return;
      } else if (newVal.lastChange === "transportelementeLabel") {
        teLabelLayer.setVisible(newVal.transportelementeLabel);
        return;
      } else if (newVal.lastChange === "bauwerkeLabel") {
        bwLabelLayer.setVisible(newVal.bauwerkeLabel);
        return;
      } 
    }, {deep: true})

    watch(() => props.backgroundlayers, (newVal) => { 
      if (!newVal) {
        return;
      }
      for (let layer of newVal) {
        layer.layer.set('name', 'backgroundlayer');
        layer.layer.set('id', layer.id);
        layer.layer.setZIndex(1);
        map.addLayer(layer.layer);
        backgroundlayers.value.push(layer.layer);
      }
    }, {deep: true});

    watch(() => props.additionalLayers, (newVal) => { 
      if (!newVal) {
        return;
      }
      for (let al of newVal) {
        al.layer.set('id', al.id);
        al.layer.set('name', 'additionalLayer');
        al.layer.set('label', al.label);
        map.addLayer(al.layer);
      }
    }, {deep: true});

    watch(() => props.selectedBackgroundLayer, (newVal) => { 
      if (!newVal) {
        return;
      }
      for (let layer of backgroundlayers.value) {
        if (layer.get('id') !== newVal.id) {
          layer.setVisible(false);
        } else {
          layer.setVisible(true);
        }
      }
    });

    watch(() => props.toggleAdditionalLayer, (newVal) => { 
      if (!newVal) {
        return;
      }
      for (let layer of map.getLayers().getArray()) {
        if (layer.get('name') === 'additionalLayer' && layer.get('id') === newVal.lastChange) {
          layer.setVisible(newVal[layer.get('id')]);
        }
      }
    }, {deep: true});

    function mapSystemelements(systemElements) {
      try {
        let seMap = {};
        for (let se of systemElements) {
          seMap[se.ID_SEBezeichnung] = {
            ID_SEBezeichnung: se.ID_SEBezeichnung,
            ID_Abflussziel: se.ID_Abflussziel,
            ID_SEKmAbfluss: se.ID_SEKmAbfluss,
            ID_TE_KanalstauraumUeberlaufZiel: se.ID_Transportelemente?.ID_TE_KanalstauraumUeberlaufZiel,
            ID_KanalKmUeberlauf: se.ID_Transportelemente?.ID_KanalKmUeberlauf,
            ID_TE_SpeicherZielUeberlauf: se.ID_Transportelemente?.ID_TE_SpeicherZielUeberlauf,
            ID_SpeicherKmUeberlauf: se.ID_Transportelemente?.ID_SpeicherKmUeberlauf,
            ID_TE_SpeicherZielEntnahme: se.ID_Transportelemente?.ID_TE_SpeicherZielEntnahme,
            ID_SpeicherKmEntnahme: se.ID_Transportelemente?.ID_SpeicherKmEntnahme,
            ID_TE_AbzweigZielNebenabfluss: se.ID_Transportelemente?.ID_TE_AbzweigZielNebenabfluss,
            ID_AbzweigKmSeitab: se.ID_Transportelemente?.ID_AbzweigKmSeitab,
            ID_TE_Typ: se.ID_Transportelemente?.ID_TE_Typ,
            geometry: se.geometry,
            U_Ref: {},
            O_Ref: {
              ID_Abflussziel: [],
              ID_TE_KanalstauraumUeberlaufZiel: [],
              ID_KanalKmUeberlauf: [],
              ID_TE_SpeicherZielUeberlauf: [],
              ID_SpeicherKmUeberlauf: [],
              ID_TE_SpeicherZielEntnahme: [],
              ID_SpeicherKmEntnahme: [],
              ID_TE_AbzweigZielNebenabfluss: [],
              ID_AbzweigKmSeitab: [],
              ALL: []
            }
          };
        }
        //Finde direkte Unter- und Oberlieger und trage Refrenzen ein
        for (let seOuter of Object.keys(seMap)) {
          for (let seInner of Object. keys(seMap)) {
            //Finde direkte Oberlieger
            if (seMap[seInner].ID_Abflussziel === seOuter) {
              seMap[seOuter].O_Ref.ID_Abflussziel.push(seMap[seInner]);
              seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_TE_KanalstauraumUeberlaufZiel === seOuter) {
              seMap[seOuter].O_Ref.ID_TE_KanalstauraumUeberlaufZiel.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_KanalKmUeberlauf === seOuter) {
              seMap[seOuter].O_Ref.ID_KanalKmUeberlauf.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_TE_SpeicherZielUeberlauf === seOuter) {
              seMap[seOuter].O_Ref.ID_TE_SpeicherZielUeberlauf.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_SpeicherKmUeberlauf === seOuter) {
              seMap[seOuter].O_Ref.ID_SpeicherKmUeberlauf.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_TE_SpeicherZielEntnahme === seOuter) {
              seMap[seOuter].O_Ref.ID_TE_SpeicherZielEntnahme.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_SpeicherKmEntnahme === seOuter) {
              seMap[seOuter].O_Ref.ID_SpeicherKmEntnahme.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_TE_AbzweigZielNebenabfluss === seOuter) {
              seMap[seOuter].O_Ref.ID_TE_AbzweigZielNebenabfluss.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            if (seMap[seInner].ID_AbzweigKmSeitab === seOuter) {
              seMap[seOuter].O_Ref.ID_AbzweigKmSeitab.push(seMap[seInner]);
              if (seMap[seOuter].O_Ref.ALL.indexOf(seMap[seInner]) === -1)
                seMap[seOuter].O_Ref.ALL.push(seMap[seInner]);
            }
            //Finde direkte Unterlieger
            if (seInner === seMap[seOuter].ID_Abflussziel) {
              seMap[seOuter].U_Ref.ID_Abflussziel = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_TE_KanalstauraumUeberlaufZiel) {
              seMap[seOuter].U_Ref.ID_TE_KanalstauraumUeberlaufZiel = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_KanalKmUeberlauf) {
              seMap[seOuter].U_Ref.ID_KanalKmUeberlauf = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_TE_SpeicherZielUeberlauf) {
              seMap[seOuter].U_Ref.ID_TE_SpeicherZielUeberlauf = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_SpeicherKmUeberlauf) {
              seMap[seOuter].U_Ref.ID_SpeicherKmUeberlauf = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_TE_SpeicherZielEntnahme) {
              seMap[seOuter].U_Ref.ID_TE_SpeicherZielEntnahme = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_SpeicherKmEntnahme) {
              seMap[seOuter].U_Ref.ID_SpeicherKmEntnahme = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_TE_AbzweigZielNebenabfluss) {
              seMap[seOuter].U_Ref.ID_TE_AbzweigZielNebenabfluss = seMap[seInner];
            }
            if (seInner === seMap[seOuter].ID_AbzweigKmSeitab) {
              seMap[seOuter].U_Ref.ID_AbzweigKmSeitab = seMap[seInner];
            }
          }
        }
        return seMap;
      }
      catch (error){
        console.error('Error:', error);
      }
    }
    
    let hoverMarkerSource = new VectorSource({}); //Hover über Längsschnitt/Gewässer Marker
    let hoverMarkerLayer : VectorLayer = new VectorLayer({
      source: hoverMarkerSource,
      style: hoverMarkerStyle,
      zIndex: 16
    });
    let gwKmMarkerSource = new VectorSource({}); //Klick in Längsschnit/Gewässer Marker
    let gwKmMarkerLayer : VectorLayer = new VectorLayer({
      source: gwKmMarkerSource,
      style: hoverMarkerStyle,
      zIndex: 17
    });
    let tgVectorSource : VectorSource = new VectorSource({}); //Natürliche Teilgebiete
    let tgLayer : VectorLayer = new VectorLayer({
      source: tgVectorSource,
      style: tgStyle,
      zIndex: 6
    });
    let tgLabelVectorSource : VectorSource = new VectorSource({}); //Natürliche Teilgebiete Label 
    let tgLabelLayer : VectorLayer = new VectorLayer({
      source: tgLabelVectorSource,
      visible: false,
      style: function(feature) {
        return new Style({
          text: new Text({
            text: feature.getId(),
            backgroundFill: new Fill({
              color: 'white'
            })
          })
        })
      },
      zIndex: 8
    });
    let kFVectorSource : VectorSource = new VectorSource({}); //Kanalisierte Flächen
    let kFLayer : VectorLayer = new VectorLayer({
      source: kFVectorSource,
      style: function(feature) {
        return setKfStyle(feature);
      },
      zIndex: 5
    });
    let kfLabelVectorSource : VectorSource = new VectorSource({}); //Kanal. Flächen Label 
    let kfLabelLayer : VectorLayer = new VectorLayer({
      source: kfLabelVectorSource,
      visible: false,
      style: function(feature) {
        return new Style({
          text: new Text({
            text: feature.getId(),
            backgroundFill: new Fill({
              color: 'white'
            })
          })
        })
      },
      zIndex: 7
    });
    let teVectorSource : VectorSource = new VectorSource({}); //Transportelemente
    let teLayer : VectorLayer = new VectorLayer({
      source: teVectorSource,
      style: teStyle,
      zIndex: 9
    });
    let teLabelVectorSource : VectorSource = new VectorSource({}); //Transportelemente Label 
    let teLabelLayer : VectorLayer = new VectorLayer({
      source: teLabelVectorSource,
      visible: false,
      style: function(feature) {
        return new Style({
          text: new Text({
            text: feature.getId(),
            backgroundFill: new Fill({
              color: 'white'
            })
          })
        })
      },
      zIndex: 12
    });
    let bauwerkeVectorSource : VectorSource = new VectorSource({}); //Bauwerke
    let bauwerkeLayer : VectorLayer = new VectorLayer({
      source: bauwerkeVectorSource,
      style:  function (feature) {
        return setBauwerkStyle(feature);
      },
      zIndex: 14
    });
    let bwLabelVectorSource : VectorSource = new VectorSource({}); //Bauwerke Label 
    let bwLabelLayer : VectorLayer = new VectorLayer({
      source: bwLabelVectorSource,
      visible: false,
      style: function(feature) {
        return new Style({
          text: new Text({
            text: feature.getId(),
            backgroundFill: new Fill({
              color: 'white'
            })
          })
        })
      },
      zIndex: 15
    });
    let gwVectorSource : VectorSource = new VectorSource({}); //Gewässer
    let gwLayer : VectorLayer = new VectorLayer({
      source: gwVectorSource,
      style: gwStyle,
      zIndex: 10
    });
    let gwLabelVectorSource : VectorSource = new VectorSource({}); //Gewässer Label 
    let gwLabelLayer : VectorLayer = new VectorLayer({
      source: gwLabelVectorSource,
      visible: false,
      style: function(feature) {
        return new Style({
          text: new Text({
            placement: 'line',
            fill: new Fill({
                color: 'white'
            }),
            stroke: new Stroke({
                color: 'black',
                width: 2
            }),
            text: feature.get('name'),
          })
        })
      },
      zIndex: 11
    });
    let sysPlanVectorSource : VectorSource = new VectorSource({}); //Systemplan der Bauwerke
    let sysPlanLayer : VectorLayer = new VectorLayer({
      source: sysPlanVectorSource,
      zIndex: 13
    });

    let mapControls = defaultControls().extend([new ScaleLine()]); //Maßstab

    onMounted(() => {
      map = new Map({
      target: 'map',
      controls: mapControls,
      layers: [
          kFLayer,
          tgLayer,
          kfLabelLayer,
          tgLabelLayer,
          teLayer,
          gwLayer,
          gwLabelLayer,
          teLabelLayer,
          sysPlanLayer,
          bauwerkeLayer,
          bwLabelLayer,
          hoverMarkerLayer,
          gwKmMarkerLayer
      ],
      view: new View({
          center: [0, 0],
          zoom: 2
      })
    })


    map.on('click', function(e) {
      clickInfoMain.value.emptyAll();
      clickInfoMain.value.weiterfuehrendeAktion = false;
      let clickOnLayerOtherThanGewaesser : Boolean = false;
      let newGewaesserSelectedOnThisClick : Boolean = false;
      map.forEachFeatureAtPixel(e.pixel, function(feature, layer) {
        if (layer === bauwerkeLayer) {
          clickOnLayerOtherThanGewaesser = true;
          feature.setStyle(setBauwerkSelectStyle(feature));
          clickInfoMain.value.pushBauwerk(feature);
          
        }
        if (layer === tgLayer) {
          clickOnLayerOtherThanGewaesser = true;
          clickInfoMain.value.pushTeilgebiet(feature);
          let transportelementInTg = teVectorSource.getFeatureById(feature.getId());
          if (transportelementInTg) {
            //Zugehöriges Transportelement auch mit in die Info nehmen
            clickInfoMain.value.pushTransportelement(transportelementInTg);
          }
          feature.setStyle(tgSelectStyle);
        }
        if (layer === teLayer) {
          clickInfoMain.value.pushTransportelement(feature);
          feature.setStyle(teSelectStyle)
          clickOnLayerOtherThanGewaesser = true;
        }
        if (layer === gwLayer) {
          if (!newGewaesserSelectedOnThisClick) { //in einem Klick soll immer nur eine Gewässerauswahl registriert werden
            newGewaesserSelectedOnThisClick = true;
            gwKmMarkerSource.clear();
            let nr = feature.getId();
            if (props.selectedData.getGewaesser() && props.selectedData.getGewaesser() != nr) { 
              unhighlightGewaesser(props.selectedData.getGewaesser()); //für aktuell ausgewähltes Gewässer Highlight entfernen
              emit('gwClickMarker', undefined); //bleibenden Marker für gewässerKm entfernen
            }
            let coords = feature.getGeometry()?.getClosestPoint((e.coordinate));
            if (props.selectedData.getGewaesser() && props.selectedData.getGewaesser() == nr) { //Beim erneuten Klick in das selected gewässer bleibenden Marker setzen
              let point = new Point(coords, "XYM");
              let marker = new Feature({ geometry: point });
              gwKmMarkerSource.addFeature(marker);
              emit('gwClickMarker', coords[2]);
            }
            highlightGewaesser(nr); //highlight für neues Gewässer
            clickInfoMain.value.setGewaesserKm(coords[2]/1000);
            clickInfoMain.value.setGewaesser(feature);
            clickInfoMain.value.setAbfluss(undefined);
            emit("gewaesserClicked", nr); //Von außen wird so das neue selectedgewaesser gesetzt

          }
        }
        if (layer === kFLayer) {
          clickOnLayerOtherThanGewaesser = true;
          feature.setStyle(setKfSelectStyle(feature));
          clickInfoMain.value.pushKanalisierteFlaeche(feature);
        }
        clickInfoMain.value.setVisible();
      }, { hitTolerance: 5 } );

      if (!newGewaesserSelectedOnThisClick && !clickOnLayerOtherThanGewaesser) { //Weder Klick auf Layer für Info, noch auf Gewässer
        clickInfoMain.value.setInvisible();
        clickInfoMain.value.emptyAll();
        hoverMarkerSource.clear();
        emit("clickInMap"); 
        gwKmMarkerSource.clear();
      }
    });

    map.on('pointermove', function(e) {
      hoverMarkerSource.clear();
      let hit = false;
      let coords;
      map.forEachFeatureAtPixel(e.pixel, function(feature, layer) {
        if (layer === gwLayer || layer === bauwerkeLayer || layer === tgLayer || layer === kFLayer || layer === teLayer) {
          hit = true;
        }
        if (feature === selectedGewaesserFeature.value) {
          coords = feature.getGeometry()?.getClosestPoint((e.coordinate));
          emit('gwHover', coords[2]);
          let point = new Point(coords, "XYM");
          let marker = new Feature({ geometry: point });
          hoverMarkerSource.addFeature(marker);
        } 
        if (!coords) {
          //Es wurde außerhalb eines Gewässers gehovered.
          //Das emit gwHover undefined signalisiert dem Längsschnitt, dass der hoverMarker entfernt werden sollte
          emit('gwHover', undefined);
        }
      }, { hitTolerance: 5 }); 
      if (hit) {
          map.getTargetElement().style.cursor = 'pointer';
      } else {
          map.getTargetElement().style.cursor = '';
      }
    });

  });
  


    function getTeilgebiete(teilGebiete, systemElements) {
      try {
        for (let tg of Object.keys(teilGebiete)) {
          let geom = new GeoJSON({dataProjection: "EPSG:3857"}).readGeometry(teilGebiete[tg]);
          if (!geom) {
            continue;
          }
          let feature = new Feature({ geometry: geom});
          let labelFeature = new Feature({ geometry: geom});
          for (let se of systemElements) {
            if (se.ID_SEBezeichnung === tg) {
              feature.setProperties({
                "ID_SEBezeichnung": se.ID_Teilgebiete?.ID_SEBezeichnung,
                "ID_TG_Typ": se.ID_Teilgebiete?.ID_TG_Typ,
                "ID_TG_Bezeichnung": se.ID_Teilgebiete?.ID_TG_Bezeichnung,
                "ID_TG_Groesse": se.ID_Teilgebiete?.ID_TG_Groesse,
                "ID_TG_Groesse_ha": se.ID_Teilgebiete?.ID_TG_Groesse ? se.ID_Teilgebiete?.ID_TG_Groesse * 0.0001 : undefined,
                "ID_Abflussziel": se.ID_Abflussziel,
                "ID_SEKommentar": se.ID_SEKommentar
              })
              feature.setId(tg);
              labelFeature.setId(tg);
              if (se.ID_Teilgebiete?.ID_TG_Typ === "natürliches TG") {
                feature.setProperties({"SE_Typ": Constants.SETypen.NatuerlichesTG})
                tgVectorSource.addFeature(feature);
                tgLabelVectorSource.addFeature(labelFeature);
              } else if (se.ID_Teilgebiete?.ID_TG_Typ === "Kanalisierte Fläche") {
                feature.setProperties({
                  "ID_TGQTMode": se.ID_Teilgebiete?.ID_TGQTMode,
                  "SE_Typ": Constants.SETypen.KanalisierteFlaeche,
                  "ID_TGEinwohner": se.ID_Teilgebiete?.ID_TGEinwohner,
                  "ID_TG_AnteilVersieglungRO": se.ID_Teilgebiete?.ID_TG_AnteilVersieglungRO
                })
                kFVectorSource.addFeature(feature);
                kfLabelVectorSource.addFeature(labelFeature);
              }
              break;
            }
          }
        }
        if (kFVectorSource.getFeatures().length > 0) {
          availableLayers.value.push(Constants.layer.kanalFlaechen);
        }
        if (tgVectorSource.getFeatures().length > 0) {
          availableLayers.value.push(Constants.layer.teilgebiete);
        }
        map.getView().fit(tgVectorSource.getExtent());
      }
      catch (error){
        console.error('Error:', error);
      }
    }

    function getTransportelemente(transportElemente, systemElements) {
      try {
        for (let te of Object.keys(transportElemente)) {
          let geom = new GeoJSON({dataProjection: "EPSG:3857"}).readGeometry(transportElemente[te]);
          if (geom) {
            teVectorSource.set('mindestensEinTransportelementMitGeometrie', true);
          }
          let feature = new Feature({ geometry: geom});
          let labelFeature = new Feature({ geometry: geom});
          //gewässernummer in systemelements suchen
          let gewaesserNr : string = "";
          for (let se of systemElements) {            
            if (se.ID_Transportelemente?.ID_SEBezeichnung === te) {
              gewaesserNr = se.ID_GewaessernummerAb;
              feature.setProperties({
                "gewaesserNr": gewaesserNr,
                "SE_Typ": Constants.SETypen.Transportelement,
                "ID_SEBezeichnung": te,
                "ID_TE_Bezeichnung": se.ID_Transportelemente?.ID_TE_Bezeichnung,
                "ID_TE_Typ": se.ID_Transportelemente?.ID_TE_Typ,
                "ID_TE_KanalstauraumUeberlaufZiel": se.ID_Transportelemente?.ID_TE_KanalstauraumUeberlaufZiel,
                "ID_KanalKmUeberlauf": se.ID_Transportelemente?.ID_KanalKmUeberlauf,
                "ID_TE_SpeicherZielUeberlauf": se.ID_Transportelemente?.ID_TE_SpeicherZielUeberlauf,
                "ID_SpeicherKmUeberlauf": se.ID_Transportelemente?.ID_SpeicherKmUeberlauf,
                "ID_TE_SpeicherZielEntnahme": se.ID_Transportelemente?.ID_TE_SpeicherZielEntnahme,
                "ID_SpeicherKmEntnahme": se.ID_Transportelemente?.ID_SpeicherKmEntnahme,
                "ID_TE_AbzweigZielNebenabfluss": se.ID_Transportelemente?.ID_TE_AbzweigZielNebenabfluss,
                "ID_AbzweigKmSeitab": se.ID_Transportelemente?.ID_AbzweigKmSeitab,
                "ID_SEKommentar": se.ID_SEKommentar
              });
              feature.setId(te);
              labelFeature.setId(te);
              break;
            }
          }
          
          teVectorSource.addFeature(feature);
          teLabelVectorSource.addFeature(labelFeature);
        }
        if (teVectorSource.getFeatures().length > 0 && teVectorSource.get('mindestensEinTransportelementMitGeometrie') == true) {
          availableLayers.value.push(Constants.layer.transportelemente);
        }
      }
      catch (error){
        console.error('Error:', error);
      }
    }

    function getBauwerke(systemElements) {
      for (let se of systemElements) {  
        if (se.ID_Transportelemente) {
          if (se.ID_Transportelemente.ID_TE_Typ.includes("Speicher") || se.ID_Transportelemente.ID_TE_Typ === "Kläranlage" || se.ID_Transportelemente.ID_TE_Typ.includes("Abzweig")) {
            let geom = new GeoJSON({dataProjection: "EPSG:3857"}).readGeometry(se.geometry);
            if (!geom) {
              continue;
            }
            let feature = new Feature({ geometry: geom});
            let labelFeature = new Feature({ geometry: geom});
            feature.setProperties({
              "ID_TE_Typ": se.ID_Transportelemente?.ID_TE_Typ,
              "ID_SEBezeichnung": se.ID_Transportelemente?.ID_SEBezeichnung,
              "ID_TE_Bezeichnung": se.ID_Transportelemente?.ID_TE_Bezeichnung,
              "ID_TE_SpeicherZielUeberlauf": se.ID_Transportelemente?.ID_TE_SpeicherZielUeberlauf,
              "ID_TE_SpeicherDrosselBeiStauziel": se.ID_Transportelemente?.ID_TE_SpeicherDrosselBeiStauziel,
              "ID_TE_SpeicherVolumen": se.ID_Transportelemente?.ID_TE_SpeicherVolumen,
              "SE_Typ": Constants.SETypen.Bauwerk,
              "ID_SEKommentar": se.ID_SEKommentar
            });
            feature.setId(se.ID_Transportelemente?.ID_SEBezeichnung);
            labelFeature.setId(se.ID_Transportelemente?.ID_SEBezeichnung);
            bauwerkeVectorSource.addFeature(feature); 
            bwLabelVectorSource.addFeature(labelFeature);
          }
        }
      }
      if (bauwerkeVectorSource.getFeatures().length > 0) {
        availableLayers.value.push(Constants.layer.bauwerke);
      }
    }

    function getGewaesser(gewaesser) {
      try {
        for (let gw of Object.keys(gewaesser)) {
          const format = new WKT();
          let geom = format.readGeometry(gewaesser[gw].geometry, {dataProjection: "EPSG:" + gewaesser[gw].srid});
          if (!geom) {
            continue;
          }
          let linestring = new LineString(geom.flatCoordinates, "XYM")
          let feature = new Feature({ geometry: linestring });
          let labelFeature = new Feature({ geometry: linestring});
          feature.setProperties({
            "name": gewaesser[gw].name,
            "SE_Typ": Constants.SETypen.Gewaesser
          });
          feature.setId(gw);
          labelFeature.setProperties({
            "name": gewaesser[gw].name
          })
          gwVectorSource.addFeature(feature);
          gwLabelVectorSource.addFeature(labelFeature);
        }
        if (gwVectorSource.getFeatures().length > 0) {
          availableLayers.value.push(Constants.layer.gewaesser);
        }
      }
      catch (error){
        console.error('Error:', error);
      }
    }

    async function init(){
      const fetchJsonOrEmptyArray = async (url: string) => {
        try {
          return await apifetch(url, null, true).then(r => r.json());
        } catch (error) {
          console.warn(`Die .json Datei wurde nicht gefunden: ${url}`, error);
          return [];
        }
      };

      const [systemElements, transportElemente, teilGebiete, gewaesser] = await Promise.all([
        fetchJsonOrEmptyArray(baseConfig.getBaseURL() + "modeldata/se.json"),
        fetchJsonOrEmptyArray(baseConfig.getBaseURL() + "modeldata/TEGeom.json"),
        fetchJsonOrEmptyArray(baseConfig.getBaseURL() + "modeldata/TGGeom.json"),
        fetchJsonOrEmptyArray(baseConfig.getBaseURL() + "modeldata/GewaesserGeom.json")
      ])
      getTransportelemente(transportElemente, systemElements);
      getBauwerke(systemElements);
      getTeilgebiete(teilGebiete, systemElements);
      getGewaesser(gewaesser);
      seMap = mapSystemelements(systemElements);
      getSystemplan(seMap);
      emit("availableLayers", availableLayers.value);
    }
    init();    

    return {
      clickInfoMain,
      highlightEinzugsgebiet,
      unhighlightEinzugsgebiet,
      availableLayers,
    }
  }
}
</script>
<style>

</style>